import { stringDecrypt, stringEncrypt } from "../utils/helpers";

class StorageService {
  private storage: Storage;
  private prefix = "v1";
  private dbPromise: Promise<IDBDatabase>;
  private cache = new Map<string, any>();

  private readonly dbName = "StorageServiceDB";
  private readonly storeName = "kv";
  private readonly dbVersion = 1;

  constructor() {
    this.storage = window.localStorage;
    this.dbPromise = this.openIndexedDB();
  }

  private getKey(key: string): string {
    return `${key}/${this.prefix}`;
  }

  public getItem<T>(key: string, encrypt = true): T | null {
    const data = this.storage.getItem(this.getKey(key)) as T | null;

    if (encrypt) {
      const decryptedData = data && stringDecrypt(data);
      return decryptedData && JSON.parse(decryptedData);
    } else {
      return data;
    }
  }

  public setItem(key: string, value: string, encrypt = true): void {
    const payload = encrypt ? stringEncrypt(value) : value;
    this.storage.setItem(this.getKey(key), payload);
  }

  public removeItem(key: string): void {
    this.storage.removeItem(this.getKey(key));
  }

  public clear(): void {
    this.storage.clear();
  }

  private openIndexedDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  public async getItemFromIDB<T>(key: string): Promise<T | null> {
    const fullKey = this.getKey(key);
    if (this.cache.has(fullKey)) {
      return this.cache.get(fullKey) as T;
    }

    const db = await this.dbPromise;
    const tx = db.transaction(this.storeName, "readonly");
    const store = tx.objectStore(this.storeName);
    const request = store.get(fullKey);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const data = request.result as string | undefined;
        if (!data) return resolve(null);
        try {
          const parsed = JSON.parse(data);
          this.cache.set(fullKey, parsed);
          resolve(parsed);
        } catch {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  public async setItemToIDB(key: string, value: string): Promise<void> {
    const fullKey = this.getKey(key);

    this.cache.set(fullKey, JSON.parse(value));

    const db = await this.dbPromise;
    const tx = db.transaction(this.storeName, "readwrite");
    const store = tx.objectStore(this.storeName);
    store.put(value, fullKey);

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  public async removeItemFromIDB(key: string): Promise<void> {
    const fullKey = this.getKey(key);
    this.cache.delete(fullKey);

    const db = await this.dbPromise;
    const tx = db.transaction(this.storeName, "readwrite");
    const store = tx.objectStore(this.storeName);
    store.delete(fullKey);

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  public async clearIDB(): Promise<void> {
    this.cache.clear();
    const db = await this.dbPromise;
    const tx = db.transaction(this.storeName, "readwrite");
    const store = tx.objectStore(this.storeName);
    store.clear();

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}

const storageService = new StorageService();
export default storageService;
